/**
 * Profiles' Water tab — profile management (PLAN §4.3: *"and also standalone
 * under Water for profile management"*). Standalone until P3; the name is the
 * one every caller and test already uses.
 *
 * This tab owns the **library**: what comes out of your tap, what came out of
 * it last March, and the classic city analyses. Recipe-specific work — dilution,
 * salts, acid, the solver — lives in the editor's panel, because those are
 * decisions about a beer, not about a water supply.
 *
 * ## The alkalinity toggle
 *
 * A water report prints alkalinity one of two ways: as CaCO₃ (the American
 * convention, and what a Ward Labs report gives) or as bicarbonate (the European
 * one). They differ by a factor of 1.219, which is exactly the sort of silent
 * 20 % error that produces a confidently wrong mash pH. So the form asks which
 * one the brewer is reading, converts on the way in, stores bicarbonate, and
 * remembers the choice so the number reads back the way it was typed.
 *
 * ## Built-ins are read-only, and say so usefully
 *
 * "Duplicate to edit" rather than a disabled Edit button: the brewer's actual
 * intent — *my water is a bit like Burton* — is one click, and the shipped
 * analyses stay byte-identical on every device, which is what keeps replay
 * deterministic.
 *
 * ## Ward Labs import
 *
 * A W-1 PDF is read in this tab and opened as a draft of the same form. The
 * two conversions a typed report gets wrong — alkalinity as CaCO₃ stored as
 * bicarbonate, and sulfate printed as SO₄-S — are applied before the brewer
 * sees the numbers, and called out so the 3× sulfate jump is not a surprise.
 */

import type { SavedWaterProfileDoc } from '@beerwright/commands';
import { toWaterProfile, toWaterProfileDoc } from '@beerwright/commands';
import { alkalinityPpm, residualAlkalinityPpm, totalHardnessPpm } from '@beerwright/core';
import {
  CopyIcon,
  LoaderCircleIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  StarIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import { useId, useRef, useState } from 'react';
import type { ExtractedWaterProfile, WaterReportPrinted } from '@/ai/water-report';
import {
  extractWaterReport,
  WATER_REPORT_MAX_CHARS,
  WATER_REPORT_MIN_CHARS,
} from '@/ai/water-report';
import { useSession } from '@/auth/session-context';
import { AssistantOrb } from '@/components/assistant-orb';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { DocsLink } from '@/components/docs-link';
import { useToast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { blankWaterProfileDoc, WaterProfileDialog } from '@/components/water/profile-dialog';
import { readFileBytes } from '@/lib/download';
import { formatNumber } from '@/lib/format';
import { newId } from '@/lib/ids';
import { userContent } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import {
  extractPdfText,
  isPdf,
  isWardLabsError,
  readWardLabsPdf,
  type WardLabsParse,
  wardLabsReviewNotice,
} from '@/lib/ward-labs';
import {
  formatIonPpm,
  ION_DISPLAY_ORDER,
  ION_SYMBOLS,
  ionOf,
  sulfateChlorideReadout,
} from '@/lib/water';
import { useDispatch } from '@/store/use-dispatch';
import { useSettings, useWaterProfiles } from '@/store/workspace-context';

export function WaterProfilesPage() {
  const profiles = useWaterProfiles();
  const session = useSession();
  // The paste path spends an assistant message, so it needs what the assistant
  // needs: a configured provider and a signed-in account to meter it against.
  const assistantAvailable = session.config.ai.mode !== 'off' && session.user !== null;
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  /** A PDF's extracted text, held as an attachment rather than shown: pages of
   *  it in a content-sized textarea made the dialog taller than the screen. */
  const [pasteFile, setPasteFile] = useState<{ name: string; text: string } | null>(null);
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteProblem, setPasteProblem] = useState<string | null>(null);
  const { dispatch } = useDispatch();
  const defaultWaterProfileId = useSettings().defaultWaterProfileId;
  const { toast } = useToast();
  const [editing, setEditing] = useState<SavedWaterProfileDoc | null>(null);
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState<SavedWaterProfileDoc | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();
  const pastePdfRef = useRef<HTMLInputElement>(null);
  const pastePdfId = useId();

  const blank = blankWaterProfileDoc;

  const openDraft = (profile: SavedWaterProfileDoc, notice: string | null = null): void => {
    setImportNotice(notice);
    setEditing(profile);
  };

  const closeDraft = (): void => {
    setEditing(null);
    setImportNotice(null);
  };

  const duplicate = (profile: SavedWaterProfileDoc): void => {
    openDraft({
      ...profile,
      id: newId('wp'),
      name: `${profile.name} (copy)`.slice(0, 120),
      builtin: false,
    });
  };

  const importReport = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setImporting(true);
    try {
      const parsed = await readWardLabsPdf(await readFileBytes(file));
      openDraft(draftFromWardLabs(blank(), parsed), wardLabsReviewNotice(parsed));
    } catch (error) {
      toast({
        variant: 'error',
        title: 'We could not read that report',
        description: isWardLabsError(error)
          ? error.message
          : 'We could not read that file. Ward Labs emails a text PDF, so a photo of the page will not work.',
      });
    } finally {
      setImporting(false);
    }
  };

  /**
   * The pasted report, through the assistant (PLAN-REMAINING C6).
   *
   * A sufficient read opens the same reviewable draft a Ward Labs PDF does —
   * an import is a draft, never a write. An insufficient one keeps the dialog
   * open with the assistant's reason under the paste, because "this report
   * does not print sodium" is an answer to act on, not an error to dismiss.
   */
  const submitPaste = async (): Promise<void> => {
    const text = pasteFile?.text ?? pasteText;
    if (pasteBusy || text.trim() === '') return;
    setPasteBusy(true);
    setPasteProblem(null);
    const result = await extractWaterReport(text);
    setPasteBusy(false);
    if (result.kind === 'sufficient') {
      setPasteOpen(false);
      setPasteText('');
      setPasteFile(null);
      openDraft(
        draftFromExtraction(blank(), result.profile),
        extractionReviewNotice(result.profile, result.printed),
      );
      return;
    }
    setPasteProblem(result.kind === 'insufficient' ? result.reason : result.message);
  };

  /**
   * A PDF chosen inside the paste dialog.
   *
   * Ward first, because Ward is free and exact: a W-1 routed through here goes
   * straight to the deterministic parser and never spends an assistant
   * message. Everything else has its text layer read out and held as an
   * **attachment chip** — nothing is sent until Read it, and nothing floods
   * the dialog either: the first draft put the extracted pages into the
   * content-sized textarea, which grew past the screen and buried the
   * buttons. A brewer who wants to trim the text pastes it themselves; the
   * chip names the file and how much text it carries, and ✕ discards it.
   * A scan or a photo has no text layer, and the dialog says so instead of
   * sending an empty paste to fail somewhere less explainable.
   */
  const choosePdfForPaste = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setPasteProblem(null);
    const bytes = await readFileBytes(file);
    if (!isPdf(bytes)) {
      setPasteProblem('That file is not a PDF. Paste the report text instead.');
      return;
    }
    try {
      const parsed = await readWardLabsPdf(bytes);
      setPasteOpen(false);
      setPasteText('');
      openDraft(draftFromWardLabs(blank(), parsed), wardLabsReviewNotice(parsed));
      return;
    } catch {
      // Not a W-1 — the ordinary case on this path. The text layer decides next.
    }
    const text = (await extractPdfText(bytes)).trim();
    if (text.length < WATER_REPORT_MIN_CHARS) {
      setPasteProblem(
        'This PDF is a picture of a page, not text, so there is nothing to read out of it. Type the numbers into a new profile instead.',
      );
      return;
    }
    setPasteFile({ name: file.name, text: text.slice(0, WATER_REPORT_MAX_CHARS) });
    setPasteText('');
  };

  /**
   * One command per save, whether the profile is new or edited.
   *
   * A create sends the whole document as `from` — the reducer replaces the id,
   * the `builtin` flag and both timestamps — so "type a report and save it" is
   * a single undo, not one command per ion.
   */
  const save = (profile: SavedWaterProfileDoc): void => {
    const existing = profiles.find((candidate) => candidate.id === profile.id);
    const ok =
      existing === undefined
        ? dispatch('waterProfile.create', {
            profileId: profile.id,
            name: profile.name,
            from: profile,
          })
        : dispatch('waterProfile.update', {
            profileId: profile.id,
            changes: {
              name: profile.name,
              notes: profile.notes,
              calciumPpm: profile.calciumPpm,
              magnesiumPpm: profile.magnesiumPpm,
              sodiumPpm: profile.sodiumPpm,
              chloridePpm: profile.chloridePpm,
              sulfatePpm: profile.sulfatePpm,
              bicarbonatePpm: profile.bicarbonatePpm,
              ph: profile.ph,
              alkalinityEntryForm: profile.alkalinityEntryForm,
            },
          });
    if (ok) closeDraft();
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* A tab body, not a page (PLAN-UX-PASS F2). This was `/water` until P3
          folded it into Profiles, and it kept its page frame: a centred
          `max-w-5xl` column inside a page that is already padded, an `<h1>`
          under the page's own, and a description that restated the tab's note.
          What is left is the actions, left-aligned and wrapping, the way the
          Mash and Fermentation tabs lay theirs out.

          The docs link comes with them. It points at the water page rather than
          the page header's Profiles one, so it is a second door and not a
          duplicate, and it belongs beside the buttons now that the heading it
          sat next to is gone. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={importing}
          onClick={() => fileInputRef.current?.click()}
        >
          {importing ? <LoaderCircleIcon className="animate-spin" /> : <UploadIcon />}
          Import Ward Labs
        </Button>
        <input
          id={fileInputId}
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="sr-only"
          aria-label="Ward Labs report"
          onChange={(event) => {
            void importReport(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        {assistantAvailable && (
          <Button
            type="button"
            variant="outline"
            data-testid="paste-report"
            onClick={() => {
              setPasteProblem(null);
              setPasteOpen(true);
            }}
          >
            <AssistantOrb />
            Paste a report
          </Button>
        )}
        <Button type="button" onClick={() => openDraft(blank())}>
          <PlusIcon />
          New profile
        </Button>
        <DocsLink docs="water" label="Water" className="ml-1" />
      </div>

      <ul className="flex flex-col gap-2">
        {profiles.map((profile) => (
          <ProfileRow
            key={profile.id}
            profile={profile}
            isDefault={profile.id === defaultWaterProfileId}
            onMakeDefault={() => dispatch('settings.update', { defaultWaterProfileId: profile.id })}
            onEdit={() => openDraft(profile)}
            onDuplicate={() => duplicate(profile)}
            onDelete={() => setDeleting(profile)}
          />
        ))}
      </ul>

      <WaterProfileDialog
        profile={editing}
        notice={importNotice}
        onClose={closeDraft}
        onSave={save}
      />

      <Dialog
        open={pasteOpen}
        onOpenChange={(open) => {
          if (pasteBusy) return;
          setPasteOpen(open);
          if (!open) {
            setPasteProblem(null);
            setPasteFile(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Paste a water report</DialogTitle>
            <DialogDescription>
              Any lab or utility report — copy the text and the assistant reads the ions out of it.
              It needs calcium, magnesium and alkalinity, which are what mash pH is made of. Sodium,
              chloride and sulfate are read when the report prints them and left empty when it does
              not. Reading one uses an assistant message.
            </DialogDescription>
          </DialogHeader>
          {pasteFile === null ? (
            <Textarea
              value={pasteText}
              rows={10}
              aria-label="Water report text"
              data-testid="paste-report-text"
              className="max-h-56 overflow-y-auto"
              placeholder={'Calcium 68 mg/L\nMagnesium 12 mg/L\nSodium…'}
              disabled={pasteBusy}
              onChange={(event) => {
                setPasteText(event.target.value);
                setPasteProblem(null);
              }}
            />
          ) : (
            <div
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
              data-testid="paste-report-file"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium" {...userContent}>
                  {pasteFile.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {pasteFile.text.length.toLocaleString('en-US')} characters of text read from the
                  PDF, ready for the assistant.
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Remove the PDF"
                data-testid="paste-report-file-remove"
                disabled={pasteBusy}
                onClick={() => {
                  setPasteFile(null);
                  setPasteProblem(null);
                }}
              >
                <Trash2Icon />
              </Button>
            </div>
          )}
          {pasteProblem !== null && (
            <p role="alert" className="text-sm text-destructive" data-testid="paste-report-problem">
              {pasteProblem}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={pasteBusy}
              data-testid="paste-report-pdf"
              onClick={() => pastePdfRef.current?.click()}
            >
              <UploadIcon />
              Read a PDF instead
            </Button>
            <input
              id={pastePdfId}
              ref={pastePdfRef}
              type="file"
              accept=".pdf,application/pdf"
              className="sr-only"
              aria-label="Water report PDF"
              data-testid="paste-report-pdf-input"
              onChange={(event) => {
                void choosePdfForPaste(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            <span className="text-xs text-muted-foreground">
              We can read a Ward Labs PDF directly at no cost. For any other PDF, we pass its text
              to the assistant.
            </span>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={pasteBusy}
              onClick={() => setPasteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={pasteBusy || (pasteFile === null && pasteText.trim() === '')}
              data-testid="paste-report-submit"
              onClick={() => void submitPaste()}
            >
              {pasteBusy ? <LoaderCircleIcon className="animate-spin" /> : <AssistantOrb />}
              {pasteBusy ? 'Reading…' : 'Read it'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete "${deleting?.name ?? ''}"?`}
        description="Recipes keep their own copy of the ions, so no recipe changes, and undo works until you reload."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting !== null) dispatch('waterProfile.delete', { profileId: deleting.id });
          setDeleting(null);
        }}
      />
    </div>
  );
}

/** One saved profile, with everything derived from it on the same line. */
function ProfileRow({
  profile,
  isDefault,
  onMakeDefault,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  readonly profile: SavedWaterProfileDoc;
  /** Whether "Set up water" on a recipe starts from this analysis. */
  readonly isDefault: boolean;
  readonly onMakeDefault: () => void;
  readonly onEdit: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
}) {
  const core = toWaterProfile(toWaterProfileDoc(profile));
  const ratio = sulfateChlorideReadout(core);

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-border bg-card/40 px-4 py-3">
      {/* Stacked on a phone: name-plus-badge and the buttons sharing one row
          leaves the name ~10 characters ("Generic 2.5 g…"), and the name is
          the only thing telling profiles apart. */}
      <div className="flex items-start justify-between gap-3 max-sm:flex-col max-sm:gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            {/* A built-in city profile's name and notes are ours, not the user's. */}
            <h2
              className="truncate text-sm font-medium max-sm:whitespace-normal"
              {...(profile.builtin ? {} : userContent)}
            >
              {profile.name}
            </h2>
            {profile.builtin && <Badge variant="muted">Built-in</Badge>}
            {/* Weaker than the equipment default on purpose: a new recipe does
                not adopt a water section at all (a recipe's water is an
                embedded copy, and "we assume nothing about your water" is the
                stated design). This is what the panel's "Set up water" button
                starts you from, instead of the built-in RO it used to
                hardcode. */}
            {isDefault && <Badge variant="muted">Starts new water sections</Badge>}
          </div>
          {profile.notes !== '' && (
            <p
              className="max-w-2xl text-xs leading-snug text-muted-foreground"
              {...(profile.builtin ? {} : userContent)}
            >
              {profile.notes}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {!isDefault && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onMakeDefault}
            >
              <StarIcon />
              <span className="max-sm:hidden">Make default</span>
              <span className="sm:hidden">Default</span>
            </Button>
          )}
          {profile.builtin ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label="Duplicate to edit"
              onClick={onDuplicate}
            >
              <CopyIcon />
              <span className="sm:hidden">Duplicate</span>
              <span className="hidden sm:inline">Duplicate to edit</span>
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={onEdit}>
              <PencilIcon />
              Edit
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Actions for ${profile.name}`}
                className="text-muted-foreground"
              >
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onSelect={onDuplicate}>
                <CopyIcon />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={profile.builtin}
                onSelect={onDelete}
              >
                <Trash2Icon />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {ION_DISPLAY_ORDER.map((ion) => (
          <span key={ion} className="font-mono text-xs tabular-nums">
            <span className="text-muted-foreground">{ION_SYMBOLS[ion]}</span>{' '}
            {/* An ion the report never printed shows the same em dash as an
                absent pH, dimmed, rather than a `0` that reads as a reading. */}
            <span className={cn(ionOf(profile, ion) === null && 'text-muted-foreground')}>
              {formatIonPpm(ionOf(profile, ion))}
            </span>
          </span>
        ))}
      </div>

      <dl className="flex flex-wrap gap-x-5 gap-y-1 border-t border-border/60 pt-2 text-[11px]">
        <Derived label="Alkalinity" value={`${formatNumber(alkalinityPpm(core), 0)} ppm CaCO₃`} />
        <Derived
          label="Residual alkalinity"
          value={`${formatNumber(residualAlkalinityPpm(core), 0)} ppm`}
          hint="Kolbach: alkalinity minus the calcium and magnesium offsets. This is what actually moves mash pH."
        />
        <Derived label="Total hardness" value={`${formatNumber(totalHardnessPpm(core), 0)} ppm`} />
        <Derived
          raw
          label="SO₄:Cl"
          value={
            ratio.ratio === null ? ratio.label : `${formatNumber(ratio.ratio, 2)} — ${ratio.label}`
          }
          {...(ratio.isFlavourClaim
            ? {}
            : {
                hint:
                  ratio.band === 'unmeasured'
                    ? 'This report does not print sulfate or chloride, so there is no balance to report. Edit the profile to add them.'
                    : 'There is too little sulfate and chloride here for their ratio to describe a flavor.',
              })}
        />
        {profile.ph !== null && <Derived label="pH" value={formatNumber(profile.ph, 1)} />}
      </dl>
    </li>
  );
}

function Derived({
  label,
  value,
  hint,
  raw = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  /** Leave the label's own casing alone — for chemical formulae like `SO₄:Cl`. */
  readonly raw?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5" title={hint}>
      <dt className={cn('tracking-wide text-muted-foreground', !raw && 'uppercase')}>{label}</dt>
      <dd className="font-mono tabular-nums">{value}</dd>
    </div>
  );
}

/** Overlay an assistant extraction onto a blank library draft. */
function draftFromExtraction(
  blank: SavedWaterProfileDoc,
  profile: ExtractedWaterProfile,
): SavedWaterProfileDoc {
  return {
    ...blank,
    name: (profile.name ?? 'Pasted report').slice(0, 120),
    notes: 'Read from a pasted report by the assistant.',
    calciumPpm: profile.calciumPpm,
    magnesiumPpm: profile.magnesiumPpm,
    sodiumPpm: profile.sodiumPpm,
    chloridePpm: profile.chloridePpm,
    sulfatePpm: profile.sulfatePpm,
    bicarbonatePpm: profile.bicarbonatePpm,
    ph: profile.ph,
    alkalinityEntryForm: profile.alkalinityEntryForm,
  };
}

/** The Ward notice's sibling: what was converted, and whose reading this is. */
function extractionReviewNotice(
  profile: ExtractedWaterProfile,
  printed: WaterReportPrinted,
): string {
  const parts: string[] = ['Read by the assistant.'];
  if (printed.notPrinted.length > 0) {
    // Named rather than silently blank: the brewer is the only one who can say
    // whether the number exists somewhere else, and they cannot go looking for
    // a gap nobody told them about.
    parts.push(
      `The report does not print ${listWords(printed.notPrinted)}, so ${printed.notPrinted.length === 1 ? 'that box is' : 'those boxes are'} left empty rather than zeroed. Fill ${printed.notPrinted.length === 1 ? 'it' : 'them'} in if you have the figure elsewhere.`,
    );
  }
  if (printed.sulfateAsSulfurPpm !== null && profile.sulfatePpm !== null) {
    parts.push(
      `Sulfate was converted from SO₄-S (${formatNumber(printed.sulfateAsSulfurPpm, 1)} ppm sulfur → ${formatNumber(profile.sulfatePpm, 1)} ppm SO₄²⁻).`,
    );
  }
  if (printed.alkalinityAsCaco3Ppm !== null) {
    parts.push(
      `Alkalinity ${formatNumber(printed.alkalinityAsCaco3Ppm, 0)} ppm as CaCO₃ is stored as ${formatNumber(profile.bicarbonatePpm, 0)} ppm bicarbonate.`,
    );
  }
  for (const entry of printed.belowDetection) {
    const ion = entry.ion.charAt(0).toUpperCase() + entry.ion.slice(1);
    parts.push(
      `${ion} was below the lab's detection limit (< ${formatNumber(entry.limitPpm, 0)} ppm) and is entered as 0.`,
    );
  }
  parts.push('Check every number against the report, then save.');
  return parts.join(' ');
}

/** `chloride`, `chloride and sulfate`, `sodium, chloride and sulfate`. */
function listWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** Overlay a Ward Labs parse onto a blank library draft. */
function draftFromWardLabs(
  blank: SavedWaterProfileDoc,
  parsed: WardLabsParse,
): SavedWaterProfileDoc {
  return {
    ...blank,
    name: parsed.name.slice(0, 120),
    notes: parsed.notes,
    calciumPpm: parsed.calciumPpm,
    magnesiumPpm: parsed.magnesiumPpm,
    sodiumPpm: parsed.sodiumPpm,
    chloridePpm: parsed.chloridePpm,
    sulfatePpm: parsed.sulfatePpm,
    bicarbonatePpm: parsed.bicarbonatePpm,
    ph: parsed.ph,
    alkalinityEntryForm: parsed.alkalinityEntryForm,
  };
}
